import React, { Component, Fragment } from "react";
import { AppCard, FormField, PageLayout } from "../../components/ui";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { utils } from "../../utils/utils";
import { toastService } from "../../services/toastService";
import { InputText } from "primereact/inputtext";
import { isKYCApproveAllowed } from "../../authGuard";
import { SelectItem } from "../../models/SelectItem";
import _ from "lodash";
import { Check, X } from "lucide-react";
import {
    AmountBlockStatuses,
    BankTransferStatus,
    BankTransferOutgoing,
    BankTransferReviewRequest,
    ReviewRosulutionStatuses,
    BankTransferSourceOfFunds,
    BankTransferRequest,
    ReconciliationStatuses,
    ReviewStatuses,
    SourceOfFundsUploadRequest,
} from "../../models/Orchestrator";
import { orchestratorApiService } from "../../services/api/Compliance/OrchestratorApiService";
import SourceOfFunds from "./SourceOfFunds";
import { confirmAction } from "../../utils/confirmHelpers";
import { InputNumber } from "primereact/inputnumber";

interface ReviewPendingBankTransferProps {
    bankTransfer: BankTransferOutgoing;
    onHide: () => void;
}

interface ReviewPendingBankTransferState {
    showConfirmDialog: boolean;
    confirmLabel: string;
    confirmAction: string;
    bankTransfer: BankTransferOutgoing;
    isLoading: boolean;
    sofDocuments: BankTransferSourceOfFunds[];
    isReadOnly: boolean;
    disableSubmitBtn: boolean;
    disableApproveBtn: boolean;
    disableRejectBtn: boolean;
    disableUnfreezeBtn: boolean;
}

export default class ReviewPendingBankTransferModal extends Component<ReviewPendingBankTransferProps, ReviewPendingBankTransferState> {
    validationErrors: Map<string, boolean> = new Map<string, boolean>();
    countryNames: Map<string, string> = new Map<string, string>();

    constructor(props: ReviewPendingBankTransferProps | Readonly<ReviewPendingBankTransferProps>) {
        super(props);

        this.state = {
            isLoading: true,
            bankTransfer: this.props.bankTransfer,
            sofDocuments: [],
            isReadOnly: false,
            disableSubmitBtn: false,
            disableApproveBtn: false,
            disableRejectBtn: false,
            disableUnfreezeBtn: false,
            showConfirmDialog: false,
            confirmAction: "",
            confirmLabel: "",
        };
    }

    sourceOfFunds: SelectItem[] = [];
    countries: SelectItem[] = [];
    financingMethods: SelectItem[] = [];
    accountPurposes: SelectItem[] = [];
    mainActivities: SelectItem[] = [];

    formatValue = (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === "") {
            return "-";
        }

        return value;
    };

    formatEnumLabel = (enumType: Record<string, string | number>, value: string | number | null | undefined) => {
        if (value === null || value === undefined) {
            return "-";
        }

        if (typeof value === "string") {
            const numericValue = Number(value);
            if (!Number.isNaN(numericValue) && enumType[numericValue] !== undefined) {
                return enumType[numericValue].toString();
            }

            return typeof enumType[value] === "number" ? value : enumType[value]?.toString() ?? value;
        }

        return enumType[value as keyof typeof enumType] ?? value.toString();
    };

    formatDateTime = (value?: string | null) => {
        if (!value) {
            return "-";
        }

        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) {
            return value;
        }

        return parsed.toLocaleString();
    };

    formatRiskRulesInfo = (riskRulesInfo: string) => {
        if (!riskRulesInfo) {
            return "";
        }

        try {
            const parsed = JSON.parse(riskRulesInfo);

            if (!Array.isArray(parsed)) {
                return riskRulesInfo;
            }

            return parsed
                .map((rule: any) => {
                    const ruleCode = rule.ruleCode ?? rule.RuleCode;
                    const ruleName = rule.ruleName ?? rule.RuleName;
                    const action = rule.action ?? rule.Action;
                    const reason = rule.reason ?? rule.Reason;
                    const matchedValue = rule.matchedValue ?? rule.MatchedValue;
                    const decisionFamily = rule.decisionFamily ?? rule.DecisionFamily;
                    const reasonCode = rule.reasonCode ?? rule.ReasonCode;
                    const source = rule.source ?? rule.Source;
                    const parts = [source, decisionFamily, ruleCode, ruleName, action, reasonCode, reason].filter(Boolean);

                    if (matchedValue) {
                        parts.push(`Matched: ${matchedValue}`);
                    }

                    return parts.join(" | ");
                })
                .join("\n");
        } catch {
            return riskRulesInfo;
        }
    };

    componentDidMount = async () => {
        this.setState({ isLoading: true });
        //........
        this.setState({ isLoading: false });
    };
    closeConfirm = () => {
        toastService.warn("Confirm Close", "Rejected");
    };
    rejectConfirmDialog = () => {
        confirmAction(
            "Are you sure you want rejected this transaction?",
            "Confirmation",
            async () => {
                await this.onRejectConfirm();
            },
        );
    };
    acceptConfirmDialog = () => {
        confirmAction(
            "Are you sure you want accept this transaction?",
            "Confirmation",
            async () => {
                await this.onApproveConfirm();
            },
        );
    };
    unfreezeConfirmDialog = () => {
        confirmAction(
            "Are you sure you want to release the outgoing freeze for this customer?",
            "Confirmation",
            async () => {
                await this.onUnfreezeConfirm();
            },
        );
    };

    onConfirm = async () => {
        switch (this.state.confirmAction) {
            case "Approve":
                await this.onApprove();
                break;
            case "Reject":
                await this.onReject();
                break;
            case "Unfreeze":
                await this.onUnfreeze();
                break;
            default:
                return;
        }
    };

    onApprove = async () => {
        this.acceptConfirmDialog();
    };

    onApproveConfirm = async () => {
        this.setState({ disableApproveBtn: true });

        //Save Notes & Documents
        const saveDraftResult = await this.onSaveDraft();
        if (!saveDraftResult) {
            this.setState({ disableApproveBtn: false });
            return;
        }

        //Approve
        const objToPost: BankTransferReviewRequest = {
            sourceEntityType: this.props.bankTransfer.sender.entityType,
            sourceEntityId: this.props.bankTransfer.sender.entityId,
            transferId: this.props.bankTransfer.id,
            reviewResolution: ReviewRosulutionStatuses.Approve,
        } as BankTransferReviewRequest;

        const result = await orchestratorApiService.setReviewResolution(objToPost);

        if (!result.success) {
            toastService.error(result.message);
            this.setState({ disableApproveBtn: false });
        } else {
            this.setState({ disableApproveBtn: false });
            this.props.onHide();
        }

        this.setState({ disableApproveBtn: false });
    };

    onReject = async () => {
        this.rejectConfirmDialog();
    };

    onRejectConfirm = async () => {
        this.setState({ disableRejectBtn: true });

        //Save Notes & Documents
        const saveDraftResult = await this.onSaveDraft();
        if (!saveDraftResult) {
            this.setState({ disableRejectBtn: false });
            return;
        }

        //Reject
        const objToPost: BankTransferReviewRequest = {
            sourceEntityType: this.props.bankTransfer.sender.entityType,
            sourceEntityId: this.props.bankTransfer.sender.entityId,
            transferId: this.props.bankTransfer.id,
            reviewResolution: ReviewRosulutionStatuses.Reject,
        } as BankTransferReviewRequest;

        const result = await orchestratorApiService.setReviewResolution(objToPost);

        if (!result.success) {
            toastService.error(result.message);
            this.setState({ disableRejectBtn: false });
        } else {
            this.setState({ disableRejectBtn: false });
            this.props.onHide();
        }

        this.setState({ disableRejectBtn: false });
    };

    onUnfreeze = async () => {
        this.unfreezeConfirmDialog();
    };

    onUnfreezeConfirm = async () => {
        this.setState({ disableUnfreezeBtn: true });

        const objToPost: BankTransferRequest = {
            sourceEntityType: this.props.bankTransfer.sender.entityType,
            sourceEntityId: this.props.bankTransfer.sender.entityId,
            transferId: this.props.bankTransfer.id,
        } as BankTransferRequest;

        const result = await orchestratorApiService.releaseCustomerOutgoingFreeze(objToPost);

        if (!result.success) {
            toastService.error(result.message);
            this.setState({ disableUnfreezeBtn: false });
        } else {
            const bankTransfer = { ...this.state.bankTransfer };
            bankTransfer.customerOutgoingFreezeActive = false;
            bankTransfer.customerOutgoingFreezeId = undefined;
            bankTransfer.customerOutgoingFreezeReasonCode = "";
            bankTransfer.customerOutgoingFreezeReasonText = "";
            bankTransfer.customerOutgoingFreezeCheckedAt = new Date().toISOString();
            this.setState({ bankTransfer, disableUnfreezeBtn: false });
            toastService.success("Outgoing freeze released.", "Action Completed");
        }

        this.setState({ disableUnfreezeBtn: false });
    };

    onSaveDraft = async () => {
        this.setState({ isLoading: true });

        //Save Documents for Source of Funds
        const allDocuments = this.state.sofDocuments;

        if (allDocuments.length > 0) {
            this.setState({ isLoading: true });

            for (const file of allDocuments) {
                if (file.isUploaded === false || file.fileStorageId === "") {
                    const fileObjToPost: SourceOfFundsUploadRequest = {
                        sourceEntityType: file.entityType,
                        sourceEntityId: file.entityId,
                        transferId: file.bankTransferId,
                        fileName: file.file?.name,
                        fileData: (await utils.convertFileToBase64(file.file!)) as string,
                    } as SourceOfFundsUploadRequest;
                    const uploadResult = await orchestratorApiService.uploadSourceOfFunds(fileObjToPost);

                    if (!uploadResult.success) {
                        this.setState({ isLoading: false });

                        toastService.error(uploadResult.message);
                        return;
                    }
                    file.isUploaded = true;

                    this.setState({ sofDocuments: allDocuments });
                }

                if (file.isModified === true) {
                    const document = await orchestratorApiService.updateSourceOfFunds(file);

                    if (!document.success) {
                        this.setState({ isLoading: false });

                        toastService.error(document.message);
                        return;
                    }
                    file.isUploaded = true;

                    this.setState({ sofDocuments: allDocuments });
                }
            }
            this.setState({ isLoading: false });
        }

        //Save Notes to Customer
        const result = await orchestratorApiService.setRiskNote(this.state.bankTransfer);
        if (!result.success) {
            this.setState({ isLoading: false });

            toastService.error(result.message);
            return;
        }

        this.setState({ isLoading: false });

        this.componentDidMount();
        return true;
    };

    onFieldUpdate = (fieldName: string, value: any) => {
        const bankTransfer = this.state.bankTransfer as any;
        bankTransfer[fieldName] = value;
        this.setState({ bankTransfer: bankTransfer });
    };

    onSubmitDocuments = async (documents: BankTransferSourceOfFunds[]) => {
        this.setState({ sofDocuments: documents });
    };

    render() {
        if (this.props.bankTransfer == null) {
            return <div></div>;
        }

        const isReadOnly = this.state.isReadOnly;
        return (
            <Dialog header="Review Outgoing Bank Transfer" visible={true} style={{ width: "70vw" }} modal onHide={this.props.onHide}>
                <Fragment>
                    <PageLayout noTitle>
                                    <AppCard className="mb-3">
                                        <AppCard.Body>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 p-4">
                                                <FormField label="Sender Acc">
                                                    <InputText
                                                        value={this.state.bankTransfer.sender.customerAccount}
                                                        className={"form-control"}
                                                        id="sourceCustomerAccount"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Sender IBAN" className="lg:col-span-1 xl:col-span-1">
                                                    <InputText
                                                        value={this.state.bankTransfer.sender.iban}
                                                        className={"form-control"}
                                                        id="sourceCustomerIBAN"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Sender Name" className="lg:col-span-1 xl:col-span-1">
                                                    <InputText
                                                        value={this.state.bankTransfer.sender.entityName}
                                                        className={"form-control"}
                                                        id="sourceUserCustomerName"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label={`${this.state.bankTransfer.sender.entityType} Id`}>
                                                    <InputNumber
                                                        value={this.state.bankTransfer.sender.entityId}
                                                        className={"form-control"}
                                                        id="sourceEntityId"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Transfer Id">
                                                    <InputNumber
                                                        value={this.state.bankTransfer.id}
                                                        className={"form-control"}
                                                        id="id"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Receiver BIC">
                                                    <InputText
                                                        value={this.state.bankTransfer.recipient.bic}
                                                        className={"form-control"}
                                                        id="beneficiaryCustomerBIC"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Receiver IBAN" className="lg:col-span-1 xl:col-span-1">
                                                    <InputText
                                                        value={this.state.bankTransfer.recipient.iban}
                                                        className={"form-control"}
                                                        id="beneficiaryCustomerIBAN"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Receiver Name" className="lg:col-span-1 xl:col-span-1">
                                                    <InputText
                                                        value={this.state.bankTransfer.recipient.entityName}
                                                        className={"form-control"}
                                                        id="beneficiaryCustomerName"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Notes" className="sm:col-span-2">
                                                    <InputText
                                                        value={this.state.bankTransfer.paymentNotes ?? ""}
                                                        className={"form-control"}
                                                        id="paymentNotes"
                                                        readOnly={true}
                                                    />
                                                </FormField>

                                                <FormField label="Amount">
                                                    <InputNumber
                                                        value={this.state.bankTransfer.amount}
                                                        className={"form-control"}
                                                        id="amount"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Currency" className="lg:col-span-1 xl:col-span-1">
                                                    <InputText
                                                        value={this.state.bankTransfer.currency}
                                                        className={"form-control"}
                                                        id="currency"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Amount BGN" className="lg:col-span-1 xl:col-span-1">
                                                    <InputNumber
                                                        value={this.state.bankTransfer.amountBgn ?? 0}
                                                        className={"form-control"}
                                                        id="amountBGN"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Charge">
                                                    <InputNumber
                                                        value={this.state.bankTransfer.chargeAmount ?? 0}
                                                        className={"form-control"}
                                                        id="chargeAmount"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Charge Curr">
                                                    <InputText
                                                        value={this.state.bankTransfer.chargeCurrency}
                                                        className={"form-control"}
                                                        id="chargeCurrency"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Transfer Status">
                                                    <InputText
                                                        value={this.formatEnumLabel(BankTransferStatus, this.state.bankTransfer.status)}
                                                        className={"form-control"}
                                                        id="transferStatus"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Review Status">
                                                    <InputText
                                                        value={this.formatEnumLabel(ReviewStatuses, this.state.bankTransfer.reviewStatus)}
                                                        className={"form-control"}
                                                        id="reviewStatus"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Risk Family">
                                                    <InputText
                                                        value={this.formatValue(this.state.bankTransfer.riskDecisionFamily)}
                                                        className={"form-control"}
                                                        id="riskDecisionFamily"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Risk Reason Code">
                                                    <InputText
                                                        value={this.formatValue(this.state.bankTransfer.riskReasonCode)}
                                                        className={"form-control"}
                                                        id="riskReasonCode"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Risk Reason Text" className="sm:col-span-2">
                                                    <InputText
                                                        value={this.formatValue(this.state.bankTransfer.riskReasonText)}
                                                        className={"form-control"}
                                                        id="riskReasonText"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Amount Block Status">
                                                    <InputText
                                                        value={this.formatEnumLabel(AmountBlockStatuses, this.state.bankTransfer.amountBlockStatus)}
                                                        className={"form-control"}
                                                        id="amountBlockStatus"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Amount Block Number">
                                                    <InputText
                                                        value={this.formatValue(this.state.bankTransfer.amountBlockNumber)}
                                                        className={"form-control"}
                                                        id="amountBlockNumber"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Blocked Amount">
                                                    <InputNumber
                                                        value={this.state.bankTransfer.amountBlockAmount ?? 0}
                                                        className={"form-control"}
                                                        id="amountBlockAmount"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Blocked Currency">
                                                    <InputText
                                                        value={this.formatValue(this.state.bankTransfer.amountBlockCurrency)}
                                                        className={"form-control"}
                                                        id="amountBlockCurrency"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Block Created At">
                                                    <InputText
                                                        value={this.formatDateTime(this.state.bankTransfer.amountBlockCreatedAt)}
                                                        className={"form-control"}
                                                        id="amountBlockCreatedAt"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Block Released At">
                                                    <InputText
                                                        value={this.formatDateTime(this.state.bankTransfer.amountBlockReleasedAt)}
                                                        className={"form-control"}
                                                        id="amountBlockReleasedAt"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Freeze Active">
                                                    <InputText
                                                        value={this.state.bankTransfer.customerOutgoingFreezeActive ? "Yes" : "No"}
                                                        className={"form-control"}
                                                        id="customerOutgoingFreezeActive"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Freeze Reason Code">
                                                    <InputText
                                                        value={this.formatValue(this.state.bankTransfer.customerOutgoingFreezeReasonCode)}
                                                        className={"form-control"}
                                                        id="customerOutgoingFreezeReasonCode"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Freeze Reason Text" className="sm:col-span-2">
                                                    <InputText
                                                        value={this.formatValue(this.state.bankTransfer.customerOutgoingFreezeReasonText)}
                                                        className={"form-control"}
                                                        id="customerOutgoingFreezeReasonText"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Freeze Checked At">
                                                    <InputText
                                                        value={this.formatDateTime(this.state.bankTransfer.customerOutgoingFreezeCheckedAt)}
                                                        className={"form-control"}
                                                        id="customerOutgoingFreezeCheckedAt"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Reconciliation Status">
                                                    <InputText
                                                        value={this.formatEnumLabel(ReconciliationStatuses, this.state.bankTransfer.reconciliationStatus)}
                                                        className={"form-control"}
                                                        id="reconciliationStatus"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Reconciliation Reason" className="sm:col-span-2">
                                                    <InputText
                                                        value={this.formatValue(this.state.bankTransfer.reconciliationReason)}
                                                        className={"form-control"}
                                                        id="reconciliationReason"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Review Decision By">
                                                    <InputText
                                                        value={this.formatValue(this.state.bankTransfer.reviewDecisionBy)}
                                                        className={"form-control"}
                                                        id="reviewDecisionBy"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Review Decision At">
                                                    <InputText
                                                        value={this.formatDateTime(this.state.bankTransfer.reviewDecisionAt)}
                                                        className={"form-control"}
                                                        id="reviewDecisionAt"
                                                        readOnly={true}
                                                    />
                                                </FormField>

                                                <FormField label="Risk Rules Info" className="col-span-full">
                                                    <InputTextarea
                                                        value={this.formatRiskRulesInfo(this.state.bankTransfer.riskRulesInfo)}
                                                        className={"form-control"}
                                                        id="riskRulesInfo"
                                                        readOnly={true}
                                                    />
                                                </FormField>
                                                <FormField label="Notes to Customer" className="col-span-full">
                                                    <InputText
                                                        value={this.state.bankTransfer.riskNotes}
                                                        className={"form-control"}
                                                        id="chargeCurrency"
                                                        onChange={(e) => {
                                                            this.onFieldUpdate("riskNotes", e.target.value);
                                                        }}
                                                        readOnly={false}
                                                    />
                                                </FormField>
                                            </div>

                                                    <AppCard>
                                                        <AppCard.Header title="Documents" />
                                                        <AppCard.Body>
                                                            <SourceOfFunds
                                                                bankTransferData={
                                                                    {
                                                                        sourceEntityType: this.props.bankTransfer.sender.entityType,
                                                                        sourceEntityId: this.props.bankTransfer.sender.entityId,
                                                                        transferId: this.props.bankTransfer.id,
                                                                    } as BankTransferRequest
                                                                }
                                                                submitToOwner={this.onSubmitDocuments}
                                                            />
                                                        </AppCard.Body>
                                                    </AppCard>
                                            <AppCard><AppCard.Header title="Actions" /><AppCard.Body>
                                                <div className="flex flex-wrap justify-between gap-2 p-2">
                                                    {isKYCApproveAllowed() && (
                                                        <div className="text-center">
                                                            <Button
                                                                label={"Approve"}
                                                                type="button"
                                                                icon={<Check size={16} />}
                                                                className="btn btn-success rounded-pill"
                                                                disabled={this.state.disableApproveBtn}
                                                                onClick={() => this.onApprove()}
                                                            />
                                                        </div>
                                                    )}
                                                    {isKYCApproveAllowed() && (
                                                        <div className="text-center">
                                                            <Button
                                                                label={"Reject"}
                                                                type="button"
                                                                icon={<X size={16} />}
                                                                className="btn btn-danger rounded-pill"
                                                                disabled={this.state.disableRejectBtn}
                                                                onClick={() => this.onReject()}
                                                            />
                                                        </div>
                                                    )}
                                                    {isKYCApproveAllowed() && (
                                                        <div className="text-center">
                                                            <Button
                                                                label={"Release Freeze"}
                                                                type="button"
                                                                icon={<Check size={16} />}
                                                                className="btn btn-warning rounded-pill"
                                                                disabled={!this.state.bankTransfer.customerOutgoingFreezeActive || this.state.disableUnfreezeBtn}
                                                                onClick={() => this.onUnfreeze()}
                                                            />
                                                        </div>
                                                    )}
                                                    {isKYCApproveAllowed() && (
                                                        <div className="text-center">
                                                            <Button
                                                                label={"Save"}
                                                                type="button"
                                                                icon={<Check size={16} />}
                                                                className="btn btn-success rounded-pill"
                                                                disabled={this.state.disableSubmitBtn}
                                                                onClick={() => this.onSaveDraft()}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </AppCard.Body></AppCard>
                                        </AppCard.Body>
                                    </AppCard>
                    </PageLayout>
                    {this.state.showConfirmDialog && (
                        <Dialog
                            header={this.state.confirmLabel}
                            visible={true}
                            style={{ width: "20vw" }}
                            modal
                            onHide={() => {
                                this.setState({ showConfirmDialog: false });
                            }}
                        >
                            <div>
                                <AppCard.Body>
                                    <div className="flex justify-end">
                                        <Button
                                            label={"Confirm"}
                                            disabled={false}
                                            type="button"
                                            icon={<Check size={16} />}
                                            severity="success"
                                            onClick={() => this.onConfirm()}
                                        ></Button>
                                    </div>
                                </AppCard.Body>
                            </div>
                        </Dialog>
                    )}
                </Fragment>
            </Dialog>
        );
    }
}
